import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  ISeetingIndividualMutationForm,
  ISeetingIndividualResponse,
} from './types';

export const useGetLicenceConfiguration = (enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ISeetingIndividualResponse, DefaultError>({
    queryKey: ['licenceConfiguration'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Configuration/GetLicenceConfiguration`,
      });

      return responseHandler(response, {
        groupName: `Configuration - GetLicenceConfiguration`,
        onNotFound: () => {},
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const usePostDemoSeedConfiguration = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostDemoSeedConfiguration'
  );

  return useMutation<void, DefaultError, void>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('seetings.what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `Demo/SeedConfiguration`,
        body: {},
      });

      await responseHandler(response, {
        groupName: 'usePostDemoSeedConfiguration',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('seetings.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePostDemoSeedData = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostDemoSeedData');

  return useMutation<void, DefaultError, void>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('seetings.what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `Demo/SeedData`,
        body: {},
      });

      await responseHandler(response, {
        groupName: 'usePostDemoSeedData',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('seetings.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePostDemoCleanDatabase = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostDemoCleanDatabase'
  );

  return useMutation<void, DefaultError, void>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('seetings.what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `Demo/CleanDatabase`,
        body: {},
      });

      await responseHandler(response, {
        groupName: 'usePostDemoCleanDatabase',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('seetings.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePostDemoResetSalesChannelsAndProvider = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostDemoResetSalesChannelsAndProvider'
  );

  return useMutation<void, DefaultError, void>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('seetings.what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `Demo/ResetSalesChannelsAndProvider`,
        body: {},
      });

      await responseHandler(response, {
        groupName: 'usePostDemoResetSalesChannelsAndProvider',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('seetings.what'),
        })
      );
    },
    onError: dismiss,
  });
};